import React, { useState } from "react";
import { DeleteFilled, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Input,
  Modal,
  Table,
  Upload,
  message,
  Row,
  Col,
  Spin,
} from "antd";
import {
  useGetTripTypesQuery,
  useAddTripTypeMutation,
  useDeleteTripTypeMutation,
} from "../../../redux/Service/api";
import { ExclamationCircleFilled } from "@ant-design/icons";
import Heading from "../../../Components/Heading/Heading";
import ServerError from "../../../Components/utils/ServerError";

interface TripType {
  TripTypeID: number;
  TripTypeName: string;
  TripTypeImageName: string;
}

const { confirm } = Modal;

const TripTypes: React.FC = () => {
  const [openModal, setOpenModal] = useState(false);
  const [modalKey, setModalKey] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");

  const [addTripType, { isLoading }] = useAddTripTypeMutation();
  const [deleteTripType, { isLoading: delIsLoading }] =
    useDeleteTripTypeMutation();

  const {
    data: tripTypesData,
    isError: isTripTypesError,
    isLoading: isTripTypesLoading,
  } = useGetTripTypesQuery(undefined);

  const tripTypes: TripType[] = tripTypesData?.ResponseObject ?? [];

  const handleFileChange = (fileList: any) => {
    if (fileList.length > 0) {
      setFile(fileList[0].originFileObj);
    } else {
      setFile(null);
    }
  };

  const handleAddTripType = async () => {
    if (!file) {
      message.error("يرجى رفع ملف");
      return;
    }
    if (!name) {
      message.error("يرجى إدخال اسم نوع الخدمة");
      return;
    }
    const formData = new FormData();
    formData.append("TripImage", file);
    formData.append("TripName", name);

    try {
      await addTripType(formData);
      message.success("تمت إضافة نوع الخدمة بنجاح");
      setFile(null);
      setName("");
      setModalKey((prev) => prev + 1);
      setOpenModal(false);
    } catch (error) {
      console.error("خطأ أثناء إضافة نوع الخدمة:", error);
      message.error("فشل في إضافة نوع الخدمة. يرجى المحاولة مرة أخرى.");
    }
  };

  const handleDeleteTripType = (id: number) => {
    confirm({
      title: "هل أنت متأكد أنك تريد حذف هذا النوع من الرحلات؟",
      icon: <ExclamationCircleFilled />,
      content: "بالضغط على موافق سيتم حذف نوع الخدمة بشكل دائم.",
      async onOk() {
        try {
          await deleteTripType(id);
          message.success("تم حذف نوع الخدمة بنجاح");
        } catch (error) {
          console.error(error);
          message.error("فشل في حذف نوع الخدمة.");
        }
      },
    });
  };

  const columns = [
    {
      title: "الصورة",
      dataIndex: "TripTypeImageName",
      key: "TripTypeImageName",
      render: (image: string) =>
        image ? (
          <img src={image} alt="TripType" className="w-20 h-20 object-cover" />
        ) : (
          "لا توجد صورة"
        ),
    },
    {
      title: "اسم النوع",
      dataIndex: "TripTypeName",
      key: "TripTypeName",
    },
    {
      title: "الإجراءات",
      key: "actions",
      render: (_: any, tripType: TripType) => (
        <div className="flex gap-2">
          <Button
            type="text"
            icon={<DeleteFilled />}
            danger
            onClick={() => handleDeleteTripType(tripType.TripTypeID)}
          />
        </div>
      ),
    },
  ];

  return (
    <section className="p-4 section_padding mt-0 pt-0 relative">
      <Heading heading="أنواع الخدمات" />

      {isTripTypesError && <ServerError />}

      <div className="text-center my-3">
        <Button
          className="w-[150px]"
          type="primary"
          onClick={() => setOpenModal(true)}
        >
          إضافة نوع الخدمة
        </Button>
      </div>

      <div className="mx-auto">
        <Table
          columns={columns}
          dataSource={tripTypes}
          rowKey="TripTypeID"
          loading={isTripTypesLoading || delIsLoading}
          pagination={{ pageSize: 5 }}
        />
      </div>

      <Modal
        title="إضافة نوع الخدمة"
        centered
        open={openModal}
        onCancel={() => setOpenModal(false)}
        footer={null}
        key={modalKey}
      >
        <div className="flex flex-col gap-4">
          <Row gutter={16}>
            <Col span={24}>
              <Input
                size="large"
                placeholder="أدخل اسم النوع"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Upload
                customRequest={({ file, onSuccess }) => {
                  onSuccess?.("ok");
                }}
                onChange={({ fileList }) => handleFileChange(fileList)}
                listType="picture"
                maxCount={1}
              >
                <Button icon={<UploadOutlined />}>
                  رفع الملف (بحد أقصى: 1)
                </Button>
              </Upload>
            </Col>
          </Row>
          <Button
            type="primary"
            onClick={handleAddTripType}
            loading={isLoading}
          >
            إضافة
          </Button>
        </div>
      </Modal>
    </section>
  );
};

export default TripTypes;
